import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Project } from '@repo/api-gw-sdk';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { Panels } from '@/components/panels/panels';
import { Link } from '@/components/shared/link';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { getUserName, UserCircle } from '@/components/users/userBadge';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import { ChangeScope, RolesSelect } from './changeScope';

export const UserBox = ({
  setForceOpen,
  hasAccountRoles,
  projects,
}: {
  setForceOpen: (open: boolean) => void;
  projects?: Project[];
  hasAccountRoles: boolean;
}) => {
  const router = useRouter();
  const { user, currentProjectId, isEmployee } = useUser();
  const [mode, setMode] = useState('default');
  const [showEnvParams, setShowEnvParams] = useState(false);
  const { rightPanel } = useWorkspace();

  const dal = useDAL();
  const { data: limitsData } = dal.inventory.limits.useLimit();

  const editProjectLimits = () => {
    rightPanel.setComponent({
      panel: Panels.ConfigureProjectLimitsWizard,
      props: {
        onSave: async () => {},
        onAbort: () => rightPanel.setIsOpen(false),
      },
    });
    rightPanel.setIsOpen(true, PanelSize.Full);
  };

  return (
    <Box
      className='user-box slide-left collapse opacity-0 absolute top-[12px] left-[24px] right-[24px] rounded-[8px] z-5'
      sx={(theme) => ({
        boxShadow: 'var(--mui-customShadows-panelMd)',
        background:
          mode === 'default' || theme.palette.mode === 'dark'
            ? 'var(--mui-palette-custom-BLACK-02)'
            : 'white',
      })}
    >
      <Stack className='w-[257px] pl-[13px] pr-[16px] py-[16px]' gap='16px'>
        {mode === 'default' && (
          <>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
            >
              <Stack direction='row' alignItems='center'>
                <UserCircle user={user} />
                <OverflowTooltip
                  text={getUserName(user)}
                  renderContent={(text, ref) => (
                    <Typography
                      className='font-semibold text-white ml-[12px] truncate'
                      ref={ref}
                      sx={{
                        maxWidth: '150px',
                      }}
                    >
                      {text}
                    </Typography>
                  )}
                />
              </Stack>
              <OptionMenu
                iconClassName='text-white'
                iconButtonProps={{
                  sx: {
                    '&:hover': {
                      backgroundColor: `var(--mui-palette-custom-BLACK-02) !important`,
                    },
                  },
                }}
                options={[
                  ...(isEmployee ? ['Env info'] : []),
                  ...(isEmployee && limitsData
                    ? ['Configure Project Limits']
                    : []),
                  'Logout',
                ]}
                onOptionSelected={(option) => {
                  if (option === 'Env info') {
                    setShowEnvParams(true);
                  }
                  if (option === 'Configure Project Limits') {
                    editProjectLimits();
                  }

                  if (option === 'Logout') {
                    router.push('/logout');
                  }
                }}
              />
              {isEmployee && (
                <EnvParamsDialog
                  showEnvParams={showEnvParams}
                  setShowEnvParams={setShowEnvParams}
                />
              )}
            </Stack>

            {projects?.length === 1 && (
              <Tooltip title='Current role'>
                <Stack>
                  <RolesSelect />
                </Stack>
              </Tooltip>
            )}

            {projects && projects.length > 1 && (
              <Tooltip
                title='Current project and role'
                PopperProps={{ disablePortal: true }}
              >
                <Stack
                  direction='row'
                  className='rounded-[4px] p-[12px] cursor-pointer'
                  alignItems='center'
                  justifyContent='space-between'
                  sx={{
                    background: 'var(--mui-palette-custom-BLACK-03)',

                    '&:hover': {
                      background: 'var(--mui-palette-custom-BLACK-02)',
                    },
                  }}
                  onClick={() => {
                    setMode('change-scope');
                    setForceOpen(true);
                  }}
                >
                  <Stack>
                    <Typography className='text-white'>
                      {projects?.find((x) => x.id === currentProjectId)?.name}
                    </Typography>
                    <Typography
                      className='text-white font-medium mt-[4px]'
                      variant='subtitle1'
                    >
                      {user?.selectedRole?.name}
                    </Typography>
                  </Stack>
                  <i className='material-symbols-arrow-drop-down-rounded text-white w-[24px] h-[24px]' />
                </Stack>
              </Tooltip>
            )}

            {hasAccountRoles && (
              <>
                <Divider
                  sx={{
                    borderColor: 'var(--mui-palette-custom-BLACK-03)',
                  }}
                />
                <Link href='/global-management' className='h-[36px]'>
                  <Button
                    className='mx-auto'
                    sx={{
                      '&:not(.Mui-disabled):hover': {
                        backgroundColor: 'var(--mui-palette-custom-BLACK-02)',
                      },
                    }}
                  >
                    <Stack
                      gap='8px'
                      direction='row'
                      alignItems='center'
                      justifyContent='center'
                    >
                      <i className='material-symbols-globe text-white w-[20px] h-[20px]' />
                      <Typography className='text-white font-medium'>
                        Global Management
                      </Typography>
                    </Stack>
                  </Button>
                </Link>
              </>
            )}
          </>
        )}
        {mode === 'change-scope' && (
          <ChangeScope
            projects={projects || []}
            onCancel={() => {
              setMode('default');
              setForceOpen(false);
            }}
          />
        )}
      </Stack>
    </Box>
  );
};

const Row = ({
  title,
  value,
}: {
  title: string;
  value: string | undefined;
}) => (
  <Stack direction='row' className='w-full px-[24px]'>
    <Typography className='w-1/2'>{title}</Typography>
    <Typography className='w-1/2'>{value}</Typography>
  </Stack>
);

const EnvParamsDialog = ({
  showEnvParams,
  setShowEnvParams,
}: {
  showEnvParams: boolean;
  setShowEnvParams: (show: boolean) => void;
}) => {
  const dal = useDAL();
  const { user, currentProjectId } = useUser();
  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );

  return (
    <MainDialog
      isOpen={showEnvParams}
      isLoading={false}
      header={{
        onClose: () => setShowEnvParams(false),
        title: 'Environment parameters',
      }}
    >
      <Stack gap='8px' divider={<Divider />}>
        <Row title='User ID' value={user?.id} />
        <Row title='User name' value={user?.givenName} />
        <Row title='Email' value={user?.email} />
        <Row title='Project ID' value={currentProjectId} />
        <Row title='Eon account ID' value={user?.eonAccount.id} />
        <Row title='Eon account name' value={user?.eonAccount.name} />
        <Row title='Domain' value={user?.eonAccount.domain} />
        <Row
          title='AWS subscription ID'
          value={user?.eonAccount.awsSubscriptionId}
        />
        <Row
          title='Azure subscription ID'
          value={user?.eonAccount.azureSubscriptionId}
        />
        <Row
          title='GCP subscription ID'
          value={user?.eonAccount.gcpSubscriptionId}
        />
        <Row
          title='Cognito client ID'
          value={user?.eonAccount.cognitoClientId}
        />
        <Row title='User pool ID' value={user?.eonAccount.userPoolId} />
        <Row
          title='LaunchDarkly client ID'
          value={process.env.LAUNCH_DARKLY_CLIENT_ID}
        />
        <FormControlLabel
          control={<Switch className='mr-[12px] ml-[24px]' />}
          label='Dev mode'
          checked={prefResponse?.value?.isDevMode ?? false}
          onChange={(event, checked) => {
            void dal.preferences.updateUserPref(PreferencesKey.Preferences, {
              ...prefResponse?.value,
              isDevMode: checked,
            });
          }}
        />
      </Stack>
    </MainDialog>
  );
};
